"use client";

import { useTranslations } from "next-intl";
import { Fragment } from "react/jsx-runtime";
import { NFTCard, PageWrapper } from "@/components/core";
import { InfiniteScroll } from "@/components/core/InfiniteScroll/InfiniteScroll";
import {
  CheckoutProjectCard,
  CheckoutProjectCardSkeleton,
  NFTHistoryCardWrapper,
  NFTHistoryEmpty,
} from "@/components/features/fast-checkout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFetchPurchaseHistory } from "@/hooks/nft/useFetchPurchaseHistory";
import { useFetchProjectsInfinite } from "@/hooks/project/useFetchProjectsInfinite";
import { hasLoadedListItems } from "@/lib/pagination/hasLoadedListItems";

const FastCheckout = () => {
  const t = useTranslations("checkout");
  const projects = useFetchProjectsInfinite();
  const purchaseHistory = useFetchPurchaseHistory({ limit: 10 });

  return (
    <PageWrapper>
      <Tabs defaultValue="projects">
        <TabsList>
          <TabsTrigger value="projects">
            {t("fastCheckout.tabProjects")}
          </TabsTrigger>
          <TabsTrigger value="history">
            {t("fastCheckout.tabHistory")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="history" className="space-y-4">
          {purchaseHistory.isLoading && !hasLoadedListItems(purchaseHistory) ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }, (_, i) => {
                const id = `history-skeleton-${i}`;
                return (
                  <NFTCard
                    key={id}
                    variant="history"
                    nftMarketAddress={""}
                    loading={true}
                    nftCollectionAddress={""}
                  />
                );
              })}
            </div>
          ) : !purchaseHistory.data?.pages.flatMap((p) => p?.data.result)
              .length ? (
            <NFTHistoryEmpty />
          ) : (
            <div className="space-y-4">
              {purchaseHistory.data?.pages.map((page, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: pages are append-only, index is stable
                <Fragment key={`nft-history-page-${i}`}>
                  {page?.data.result?.map((nft) => (
                    <NFTHistoryCardWrapper
                      key={nft.id}
                      nftCollectionAddress={nft.nftCollectionAddress || ""}
                      nftId={nft.id}
                      ticket={nft.ticket}
                    >
                      <NFTCard
                        variant="history"
                        nftMarketAddress={nft.nftMarketAddress}
                        nftData={{
                          token_id: nft.token_id,
                          marketType: {
                            price: nft.price,
                            plan: nft.type,
                          },
                          imageUrl: nft.imageUrl ?? null,
                        }}
                        nftCollectionAddress={nft.nftCollectionAddress || ""}
                      />
                    </NFTHistoryCardWrapper>
                  ))}
                </Fragment>
              ))}
            </div>
          )}
          <InfiniteScroll query={purchaseHistory} />
        </TabsContent>
        <TabsContent value="projects" className="space-y-4 mt-6">
          <div className="flex flex-col items-start justify-start text-right space-y-1 mb-6">
            <h4 className="font-semibold text-lg text-left">
              {t("fastCheckout.heading")}
            </h4>
            <p className="text-sm">{t("fastCheckout.subtitle")}</p>
          </div>
          {projects.isLoading && !hasLoadedListItems(projects) ? (
            <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 3 }, (_, i) => {
                const id = `project-skeleton-${i}`;
                return <CheckoutProjectCardSkeleton key={id} />;
              })}
            </div>
          ) : !projects.data?.pages.flatMap((page) => page?.data?.result ?? [])
              ?.length ? (
            <p className="text-sm mt-12 text-center text-text-secondary">
              {t("fastCheckout.noProjects")}
            </p>
          ) : (
            <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {projects.data?.pages
                .flatMap((page) => page?.data?.result ?? [])
                ?.map((project) => (
                  <CheckoutProjectCard key={project.id} {...project} />
                ))}
            </div>
          )}
          <InfiniteScroll query={projects} />
        </TabsContent>
      </Tabs>
    </PageWrapper>
  );
};

export default FastCheckout;
